import { useState } from "react";
import { ChevronDown, ChevronRight, Star, TrendingUp } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useSimilarProposals } from "../hooks/useTenders";
import type { SimilarProposal } from "../types";

interface Props {
  tenderId: string;
  templateIds: string[];
}

function fmt(value: string | null): string {
  if (!value) return "—";
  const n = parseFloat(value);
  if (isNaN(n)) return value;
  return n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" }) + " zł";
}

/** „1 wspólny plik" / „2 wspólne pliki" / „5 wspólnych plików" — polska odmiana. */
function sharedFilesLabel(n: number): string {
  if (n === 1) return "1 wspólny plik";
  const teen = n % 100 >= 12 && n % 100 <= 14;
  const last = n % 10;
  if (!teen && last >= 2 && last <= 4) return `${n} wspólne pliki`;
  return `${n} wspólnych plików`;
}

function SimilarTenderRow({ item }: { item: SimilarProposal }) {
  const [expanded, setExpanded] = useState(false);
  const t = item.tender;

  return (
    <div className="rounded-lg overflow-hidden">
      {/* Summary row */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="text-gray-400">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {t.title}
            </span>
            {t.same_branch && (
              <span className="text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-[var(--color-primary)] dark:text-blue-300 px-1.5 py-0.5 rounded uppercase tracking-wide leading-none flex-shrink-0">
                ten oddział
              </span>
            )}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide leading-none flex-shrink-0 ${
              t.status === "won" || t.status === "won_unofficial"
                ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
            }`}>
              {t.status_display}
            </span>
            {item.shared_files > 0 && (
              <span
                title={item.shared_file_names.slice(0, 10).join("\n")}
                className="text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded uppercase tracking-wide leading-none flex-shrink-0"
              >
                {sharedFilesLabel(item.shared_files)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            <span>{t.client_name}{t.client_branch_name ? ` — ${t.client_branch_name}` : ""}</span>
            <span>{new Date(t.created_at).toLocaleDateString("pl-PL")}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 flex-shrink-0 text-xs">
          <div className="text-right hidden sm:block">
            <p className="text-gray-400">Wspólnych pozycji</p>
            <p className="font-semibold text-gray-700 dark:text-gray-300">
              {item.common_count}
              {item.new_count > 0 && <span className="text-[var(--color-primary)] ml-1">+{item.new_count}</span>}
              {item.removed_count > 0 && <span className="text-gray-400 ml-1">−{item.removed_count}</span>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-gray-400">Oferta</p>
            <p className="font-semibold text-green-700 dark:text-green-400">
              {fmt(item.winning_offer_value ?? item.proposal.value)}
            </p>
          </div>
        </div>
      </button>

      {/* Expanded item comparison */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-3 py-3 bg-gray-50 dark:bg-gray-800/50">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 dark:text-gray-500">
                <th className="text-left pb-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                <th className="text-right pb-1.5 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                <th className="text-right pb-1.5 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
                <th className="text-right pb-1.5 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {item.items.map((it) => (
                <tr
                  key={it.template_id}
                  className={
                    !it.in_past
                      ? "opacity-40"
                      : !it.in_current
                      ? "opacity-50"
                      : ""
                  }
                >
                  <td className="py-1 pr-2">
                    <span className="text-gray-800 dark:text-gray-200">{it.name}</span>
                    {!it.in_current && (
                      <span className="ml-1.5 text-[9px] bg-gray-200 dark:bg-gray-700 text-gray-500 px-1 rounded">usunięta</span>
                    )}
                    {!it.in_past && (
                      <span className="ml-1.5 text-[9px] bg-blue-100 dark:bg-blue-900/40 text-[var(--color-primary)] px-1 rounded">nowa</span>
                    )}
                  </td>
                  <td className="py-1 text-right text-gray-600 dark:text-gray-400">{it.past_quantity ?? "—"}</td>
                  <td className="py-1 text-right text-gray-600 dark:text-gray-400">
                    {it.past_base_rate ? `${parseFloat(it.past_base_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, useGrouping: "always" })} zł` : "—"}
                  </td>
                  <td className="py-1 text-right font-medium text-gray-800 dark:text-gray-200">
                    {it.past_line_total ? `${parseFloat(it.past_line_total).toLocaleString("pl-PL", { minimumFractionDigits: 2, useGrouping: "always" })} zł` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-600">
                <td colSpan={3} className="pt-1.5 text-gray-500 text-right pr-2">Wycena (×{item.proposal.multiplier}%)</td>
                <td className="pt-1.5 text-right font-bold text-gray-900 dark:text-gray-100">
                  {fmt(item.proposal.value)}
                </td>
              </tr>
              {item.winning_offer_value && item.winning_offer_value !== item.proposal.value && (
                <tr>
                  <td colSpan={3} className="pt-0.5 text-gray-500 text-right pr-2">Złożona oferta</td>
                  <td className="pt-0.5 text-right font-bold text-green-700 dark:text-green-400">
                    {fmt(item.winning_offer_value)}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export function SimilarProposals({ tenderId, templateIds }: Props) {
  const [open, setOpen] = useState(false);
  const { data: results = [], isLoading, isFetching } = useSimilarProposals(
    tenderId,
    templateIds,
    open
  );

  if (templateIds.length === 0 && !open) return null;

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-4 mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-medium text-[var(--color-primary)] dark:text-blue-400 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] transition-colors"
      >
        <TrendingUp size={15} />
        {open ? "Ukryj podobne przetargi" : "Pokaż podobne przetargi"}
        {open && isFetching && <span className="text-xs text-gray-400 ml-1">Ładowanie...</span>}
      </button>

      {open && (
        <div className="mt-3 flex flex-col gap-2">
          {isLoading ? (
            <div className="flex justify-center py-2"><Spinner size="sm" /></div>
          ) : results.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">
              Nie znaleziono podobnych zakończonych przetargów.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Star size={12} className="text-amber-500" />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Znaleziono {results.length} podobnych przetargów — sortowane wg trafności i oddziału
                </p>
              </div>
              {results.map((r) => (
                <SimilarTenderRow key={r.tender.id} item={r} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
